import { DialogTitle } from "@headlessui/react";

import { JSX } from "react";
import { SubmitHandler, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Button from "@/components/buttons/Button.tsx";
import Input from "@/components/inputs/Input.tsx";

import { useAddCreditCardInit } from "@/hooks/api/user.hooks";
import useModalRouter from "@/hooks/useModalRouter";
import { onError } from "@/utils/onError";
import { onSuccess } from "@/utils/onSuccess";

import { MODAL_ROUTES } from "../router/ModalRoutes";
import { CreditCardForm } from "./CreditCardModalWrap";

const CreditCardAddModal = (): JSX.Element => {
  const { t } = useTranslation();

  const { closeModal, openModal } = useModalRouter();

  const { handleSubmit, control, formState, register } = useFormContext<CreditCardForm>();

  const { mutate, isPending } = useAddCreditCardInit();

  const { errors } = formState;

  const onSubmit: SubmitHandler<CreditCardForm> = (data): void => {
    mutate(
      {
        card_number: data.card_number.replace(/\s/g, ""),
        expire_date: data.card_expiration.replace(/\s/g, "").replace("/", ""),
      },
      {
        onSuccess: (response) => {
          openModal([MODAL_ROUTES.CREDIT_CARD, MODAL_ROUTES.CREDIT_CARD_OTP], {
            transaction_id: response.data.transaction_id,
            phone: response.data.phone,
          });
          onSuccess(t("auth.otp-sent"));
        },
        onError,
      },
    );
  };

  return (
    <div className="max-w-[32.5rem] sm:w-[32.5rem]">
      <DialogTitle as={"h2"} className={"staff-modal__title text-center"}>
        {t("Payment.add-card")}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)} className={"mt-6 w-full"}>
        <Input
          name={"card_number"}
          type={"number"}
          placeholder={t("cards.number")}
          control={control}
          mask={"#### #### #### ####"}
          validation={{ required: true, minLength: 15 }}
          color={"white"}
          errors={errors}
          register={register}
          value={formState.defaultValues?.card_number}
        />
        <Input
          name={"card_expiration"}
          type={"number"}
          placeholder={t("cards.month-year")}
          control={control}
          mask={"## / ##"}
          validation={{ required: true }}
          color={"white"}
          errors={errors}
          register={register}
          className={"mt-4"}
          value={formState.defaultValues?.card_expiration}
        />
        <div className={"mt-4 grid grid-cols-2 gap-6 max-lg:gap-3"}>
          <Button color={"white"} aria-label={t("Actions.cancel")} onClick={closeModal}>
            {t("Actions.cancel")}
          </Button>
          <Button type={"submit"} aria-label={t("Actions.add")} isLoading={isPending}>
            {t("Actions.add")}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreditCardAddModal;
